import SEO from '../../components/common/SEO';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../../components/admin/EditableElement';

export default function Terms() {
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  // Helper to get content with fallback (CMS content takes priority, then i18n, then hardcoded fallback)
  const get = (key: string, fallback: string) =>
    content?.getContent('terms', key, t(`terms.${key}`, fallback)) ?? t(`terms.${key}`, fallback);

  // Helper to render list items from pipe-separated string
  const renderList = (key: string, fallback: string) => {
    const items = get(key, fallback).split('|');
    return (
      <ul className="list-disc list-inside text-gray-600 space-y-1">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <SEO page="terms" />
      <div className="py-16 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <EditableElement section="terms" field="title">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">
              {get('title', 'Terms of Service')}
            </h1>
          </EditableElement>

          <div className="prose prose-gray max-w-none">
            <EditableElement section="terms" field="last_updated">
              <p className="text-gray-600 mb-6">
                {get('last_updated', 'Last updated: December 2024')}
              </p>
            </EditableElement>

            <section className="mb-8">
              <EditableElement section="terms" field="section_1_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_1_title', '1. Acceptance of Terms')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_1_content">
                <p className="text-gray-600">
                  {get('section_1_content', 'By accessing or using RUMIO\'s mobile application, browser extension, or website (the "Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_2_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_2_title', '2. Description of Service')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_2_intro">
                <p className="text-gray-600 mb-4">
                  {get('section_2_intro', 'RUMIO provides a product tracking service that allows users to:')}
                </p>
              </EditableElement>
              {renderList('section_2_list', 'Track IKEA product availability and prices|Receive notifications about price changes and restocks|Create and manage product watchlists|View price history information')}
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_3_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_3_title', '3. No Affiliation with IKEA')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_3_content">
                <p className="text-gray-600">
                  {get('section_3_content', 'RUMIO is an independent service and is not affiliated with, endorsed by, or connected to IKEA in any way. IKEA is a registered trademark of Inter IKEA Systems B.V. Product information displayed in our Service is sourced from publicly available IKEA websites.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_4_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_4_title', '4. User Accounts')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_4_intro">
                <p className="text-gray-600 mb-4">
                  {get('section_4_intro', 'To use certain features of the Service, you must create an account. You are responsible for:')}
                </p>
              </EditableElement>
              {renderList('section_4_list', 'Maintaining the confidentiality of your account credentials|All activities that occur under your account|Notifying us immediately of any unauthorized use')}
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_5_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_5_title', '5. Subscriptions and Payments')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_5_intro">
                <p className="text-gray-600 mb-4">
                  {get('section_5_intro', 'Some features of the Service require a paid subscription.')}
                </p>
              </EditableElement>
              {renderList('section_5_list', 'Subscription fees are billed in advance on a monthly basis|All fees are non-refundable except as required by law|You may cancel your subscription at any time|We reserve the right to change pricing with notice')}
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_6_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_6_title', '6. Acceptable Use')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_6_intro">
                <p className="text-gray-600 mb-4">
                  {get('section_6_intro', 'You agree not to:')}
                </p>
              </EditableElement>
              {renderList('section_6_list', 'Use the Service for any illegal purpose|Attempt to gain unauthorized access to the Service|Interfere with or disrupt the Service|Use automated means to access the Service without permission|Resell or redistribute the Service|Violate any applicable laws or regulations')}
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_7_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_7_title', '7. Accuracy of Information')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_7_content">
                <p className="text-gray-600">
                  {get('section_7_content', 'While we strive to provide accurate product information, we do not guarantee the accuracy, completeness, or timeliness of any information displayed in the Service. Product availability and prices are subject to change without notice. Always verify information directly with IKEA before making purchasing decisions.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_8_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_8_title', '8. Intellectual Property')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_8_content">
                <p className="text-gray-600">
                  {get('section_8_content', 'The Service and its original content, features, and functionality are owned by RUMIO and are protected by international copyright, trademark, and other intellectual property laws. Product images and descriptions are the property of their respective owners.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_9_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_9_title', '9. Disclaimer of Warranties')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_9_content">
                <p className="text-gray-600">
                  {get('section_9_content', 'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_10_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_10_title', '10. Limitation of Liability')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_10_content">
                <p className="text-gray-600">
                  {get('section_10_content', 'IN NO EVENT SHALL RUMIO BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_11_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_11_title', '11. Termination')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_11_content">
                <p className="text-gray-600">
                  {get('section_11_content', 'We may terminate or suspend your account and access to the Service immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_12_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_12_title', '12. Changes to Terms')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_12_content">
                <p className="text-gray-600">
                  {get('section_12_content', 'We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the updated Terms on this page. Your continued use of the Service after changes constitutes acceptance of the modified Terms.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_13_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_13_title', '13. Governing Law')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_13_content">
                <p className="text-gray-600">
                  {get('section_13_content', 'These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which RUMIO operates, without regard to its conflict of law provisions.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="terms" field="section_14_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_14_title', '14. Contact Us')}
                </h2>
              </EditableElement>
              <EditableElement section="terms" field="section_14_content">
                <p className="text-gray-600">
                  {get('section_14_content', 'If you have any questions about these Terms, please contact us through our Support page.')}
                </p>
              </EditableElement>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
